import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { PipelineResult, SchemePageInfo, DeviceParts, SpecItem } from '../types/models';
import { getCachedPDF, cachePDF } from '../utils/pdfCache';
import { useApplicationPolling } from '../hooks/useApplicationPolling';
import { 
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  DocumentIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import PdfCropperViewer, { PdfCropperViewerHandle } from '../components/PdfCropperViewer';

// Вспомогательная функция для проверки завершения разметки (все устройства имеют chunk_gcs_uri)
const isMarkupComplete = (schemeData: SchemePageInfo | null): boolean => {
  if (!schemeData?.parts?.devices || schemeData.parts.devices.length === 0) {
    return false;
  }
  return schemeData.parts.devices.every(device => 
    device.chunk_gcs_uri !== null && device.chunk_gcs_uri !== undefined && device.chunk_gcs_uri !== ''
  );
};

// Вспомогательная функция для определения статуса разметки
const getMarkupStatus = (schemeData: SchemePageInfo | null): 'required' | 'complete' | 'not_needed' => {
  const deviceCount = schemeData?.parts?.devices?.length ?? 0;
  
  if (deviceCount === 1) {
    return 'not_needed';
  }
  
  if (deviceCount > 1) {
    return isMarkupComplete(schemeData) ? 'complete' : 'required';
  }
  
  return 'not_needed';
};

// Вспомогательная функция для проверки завершения подбора комплектующих (все specs имеют spn_id)
const isSelectionComplete = (schemeData: SchemePageInfo | null): boolean => {
  if (!schemeData?.parts?.devices || schemeData.parts.devices.length === 0) {
    return false;
  }
  
  // Проверяем что у всех устройств есть specs и у всех specs есть spn_id
  for (const device of schemeData.parts.devices) {
    if (!device.specs || device.specs.length === 0) {
      return false;
    }
    
    for (const spec of device.specs) {
      if (!spec.spn_id) {
        return false;
      }
    }
  }
  
  return true;
};

const SchemeDetailPage: React.FC = () => {
  const { appId, schemeIndex } = useParams<{ appId: string; schemeIndex: string }>();
  const navigate = useNavigate();
  const pdfCropperRef = useRef<PdfCropperViewerHandle>(null);
  
  const [pdResult, setPdResult] = useState<PipelineResult | null>(null);
  const [schemeData, setSchemeData] = useState<SchemePageInfo | null>(null);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [isPreparingChunks, setIsPreparingChunks] = useState(false);
  
  // Polling статуса заявки
  const { status } = useApplicationPolling(
    appId ? Number.parseInt(appId) : null,
    10000,  // 10 секунд
    true
  );
  
  // Состояние UI
  const [expandedDevices, setExpandedDevices] = useState<Set<number>>(new Set());
  const [editingDevice, setEditingDevice] = useState<number | null>(null);
  const [editingSpec, setEditingSpec] = useState<{ deviceIdx: number; specIdx: number } | null>(null);
  const [editValues, setEditValues] = useState<{ name?: string; qty?: number; quantity?: number; uom?: string }>({});
  const [showMarkupInstructions, setShowMarkupInstructions] = useState(false);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const [exampleZoom, setExampleZoom] = useState(100);

  useEffect(() => {
    // Получаем userId при загрузке компонента
    const fetchUserId = async () => {
      try {
        const response = await ApiService.checkAuthStatus();
        if (response.user) {
          setUserId(response.user.id);
        }
      } catch (err) {
        console.error('Error fetching user id:', err);
      }
    };
    fetchUserId();
  }, []);

  useEffect(() => {
    console.log('SchemeDetailPage useEffect triggered with params:', { appId, schemeIndex });
    if (appId && schemeIndex !== undefined) {
      // Скролл в начало страницы при загрузке
      window.scrollTo(0, 0);
      loadSchemeData(parseInt(appId), parseInt(schemeIndex));
    }
  }, [appId, schemeIndex]);
  
  // Обновляем schemeData при изменении status из polling
  useEffect(() => {
    if (status?.pd_result && schemeIndex !== undefined) {
      const index = Number.parseInt(schemeIndex);
      if (index >= 0 && index < status.pd_result.scheme_pages.length) {
        const updatedScheme = status.pd_result.scheme_pages[index];
        
        // Проверяем изменился ли статус обработки на completed
        const prevStatus = schemeData?.processing_status;
        const newStatus = updatedScheme.processing_status;
        
        setSchemeData(updatedScheme);
        
        // Если статус изменился на completed, перезагружаем PDF из кеша
        if (prevStatus === 'processing' && newStatus === 'completed' && updatedScheme.url) {
          console.log('Processing completed, reloading PDF from cache');
          
          // Асинхронно перезагружаем PDF
          (async () => {
            try {
              const cachedData = await getCachedPDF(updatedScheme.url!);
              if (cachedData) {
                console.log('Reloading PDF from IndexedDB cache after completion:', updatedScheme.url);
                // Создаём НОВЫЙ буфер
                const buffer = new ArrayBuffer(cachedData.byteLength);
                new Uint8Array(buffer).set(new Uint8Array(cachedData));
                setPdfData(buffer);
              }
            } catch (err) {
              console.error('Error reloading PDF from cache:', err);
            }
          })();
        }
      }
    }
  }, [status, schemeIndex]);

  const loadSchemeData = async (applicationId: number, index: number) => {
    setLoading(true);
    setError(null);
    try {
      // Получаем инфо о схеме
      const response = await ApiService.getPDResult(applicationId);
      if (!response.pd_result) throw new Error('PD Result not found');
      
      setPdResult(response.pd_result);
      
      if (index < 0 || index >= response.pd_result.scheme_pages.length) {
        throw new Error('Invalid scheme index');
      }
      
      const scheme = response.pd_result.scheme_pages[index];
      setSchemeData(scheme);
      
      // Грузим PDF - сначала проверяем IndexedDB кеш
      if (scheme.url) {
        try {
          // Проверяем IndexedDB кеш (поддерживает большие файлы, обычно 50MB+)
          const cachedData = await getCachedPDF(scheme.url);
          if (cachedData) {
            console.log('Loading PDF from IndexedDB cache:', scheme.url);
            // Создаём НОВЫЙ буфер (никогда не используем старый так как он может быть детачед)
            const buffer = new ArrayBuffer(cachedData.byteLength);
            new Uint8Array(buffer).set(new Uint8Array(cachedData));
            setPdfData(buffer);
          } else {
            // Нет в кеше - грузим с бэкенда
            console.log('Loading PDF from backend:', scheme.url);
            const proxyUrl = `/api/applications/${applicationId}/schemes/${index}/pdf-blob`;
            
            const response = await fetch(proxyUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ gcs_uri: scheme.url }),
            });
            
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            
            const buffer = await response.arrayBuffer();
            console.log('PDF loaded from backend, size:', buffer.byteLength, 'bytes');
            
            // Кешируем в IndexedDB (поддерживает большие файлы)
            await cachePDF(scheme.url, buffer);
            
            // Создаём копию буфера - никогда не используем напрямую от fetch
            const bufferCopy = new ArrayBuffer(buffer.byteLength);
            new Uint8Array(bufferCopy).set(new Uint8Array(buffer));
            setPdfData(bufferCopy);
          }
        } catch (err) {
          console.error('Error loading PDF:', err);
          setPdfData(null);
        }
      }
    } catch (err) {
      console.error('Error loading scheme:', err);
      setError('Ошибка при загрузке схемы');
    } finally {
      setLoading(false);
    }
  };

  const savePDResult = async (updatedResult: PipelineResult) => {
    if (!appId) return;
    
    setSaving(true);
    try {
      await ApiService.updatePDResult(parseInt(appId), updatedResult);
      setPdResult(updatedResult);
    } catch (err) {
      console.error('Error saving PD result:', err);
      setError('Ошибка при сохранении изменений');
    } finally {
      setSaving(false);
    }
  };

  const updateSchemeInPDResult = (updatedScheme: SchemePageInfo) => {
    if (!pdResult || schemeIndex === undefined) return;
    
    const newSchemePages = [...pdResult.scheme_pages];
    newSchemePages[parseInt(schemeIndex)] = updatedScheme;
    
    const updatedPdResult = {
      ...pdResult,
      scheme_pages: newSchemePages
    };
    
    setSchemeData(updatedScheme);
    savePDResult(updatedPdResult);
  };

  const toggleDevice = (index: number) => {
    const newExpanded = new Set(expandedDevices);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedDevices(newExpanded);
  };

  const startEditDevice = (deviceIdx: number, device: DeviceParts) => {
    if (isSelectionComplete(schemeData)) return;
    setEditingDevice(deviceIdx);
    setEditValues({ name: device.device_name, qty: device.qty });
  };

  const saveDeviceEdit = (deviceIdx: number) => {
    if (!schemeData || !schemeData.parts) return;
    if (isSelectionComplete(schemeData)) return;
    
    const updatedDevices = [...schemeData.parts.devices];
    updatedDevices[deviceIdx] = {
      ...updatedDevices[deviceIdx],
      device_name: editValues.name || updatedDevices[deviceIdx].device_name,
      qty: editValues.qty !== undefined ? editValues.qty : updatedDevices[deviceIdx].qty
    };
    
    const updatedScheme = {
      ...schemeData,
      parts: {
        devices: updatedDevices
      }
    };
    
    updateSchemeInPDResult(updatedScheme);
    setEditingDevice(null);
    setEditValues({});
  };

  const cancelEdit = () => {
    setEditingDevice(null);
    setEditingSpec(null);
    setEditValues({});
  };

  const deleteDevice = (deviceIdx: number) => {
    if (!schemeData || !schemeData.parts) return;
    if (isSelectionComplete(schemeData)) return;
    if (!window.confirm('Удалить это устройство и все его комплектующие?')) return;
    
    const updatedDevices = schemeData.parts.devices.filter((_, idx) => idx !== deviceIdx);
    const updatedScheme = {
      ...schemeData,
      parts: {
        devices: updatedDevices
      }
    };
    
    updateSchemeInPDResult(updatedScheme);
  };

  const addDevice = () => {
    if (!schemeData || !schemeData.parts) return;
    if (isSelectionComplete(schemeData)) return;
    
    const newDevice: DeviceParts = {
      device_name: 'Новое устройство',
      specs: [],
      qty: 1
    };
    
    const updatedScheme = {
      ...schemeData,
      parts: {
        devices: [...schemeData.parts.devices, newDevice]
      }
    };
    
    updateSchemeInPDResult(updatedScheme);
  };

  const startEditSpec = (deviceIdx: number, specIdx: number, spec: SpecItem) => {
    if (isSelectionComplete(schemeData)) return;
    setEditingSpec({ deviceIdx, specIdx });
    setEditValues({ name: spec.name, quantity: spec.quantity, uom: spec.uom });
  };

  const saveSpecEdit = async (deviceIdx: number, specIdx: number) => {
    if (!schemeData || !schemeData.parts) return;
    if (isSelectionComplete(schemeData)) return;
    if (!userId) {
      setError('Ошибка: не удалось определить пользователя');
      return;
    }
    
    const updatedDevices = [...schemeData.parts.devices];
    const updatedSpecs = [...updatedDevices[deviceIdx].specs];
    const currentSpec = updatedSpecs[specIdx];
    const currentDevice = updatedDevices[deviceIdx];
    
    // Обновляем spec локально
    updatedSpecs[specIdx] = {
      ...updatedSpecs[specIdx],
      name: editValues.name || updatedSpecs[specIdx].name,
      quantity: editValues.quantity !== undefined ? editValues.quantity : updatedSpecs[specIdx].quantity,
      uom: editValues.uom || updatedSpecs[specIdx].uom
    };
    
    // Если у spec есть spn_id, обновляем соответствующий SPN на бэкенде
    if (currentSpec.spn_id) {
      try {
        // Обновляем имя, если оно изменилось (ТРИГГЕРИТ ПОИСК!)
        if (editValues.name && editValues.name !== currentSpec.name) {
          await ApiService.updateSPNName(currentSpec.spn_id, userId, { name: editValues.name });
        }
        
        // Обновляем количество, если оно изменилось (БЕЗ ПОИСКА)
        // Важно: рассчитываем итоговое количество для SPN так же, как при создании
        if (editValues.quantity !== undefined && editValues.quantity !== currentSpec.quantity) {
          // Рассчитываем dev_quantity по той же логике, что и на бэкенде
          const devQuantity = (schemeData.qty && currentDevice.qty && currentDevice.qty < schemeData.qty) 
            ? schemeData.qty 
            : (currentDevice.qty || 1);
          
          // Итоговое количество для SPN = количество спецификации * количество устройств
          const totalQuantity = editValues.quantity * devQuantity;
          
          await ApiService.updateSPNQuantity(currentSpec.spn_id, totalQuantity);
        }
      } catch (err) {
        console.error('Error updating SPN:', err);
        setError('Ошибка при обновлении связанной позиции подбора');
        return; // Не сохраняем локально если не удалось обновить SPN
      }
    }
    
    updatedDevices[deviceIdx] = {
      ...updatedDevices[deviceIdx],
      specs: updatedSpecs
    };
    
    const updatedScheme = {
      ...schemeData,
      parts: {
        devices: updatedDevices
      }
    };
    
    updateSchemeInPDResult(updatedScheme);
    setEditingSpec(null);
    setEditValues({});
  };

  const deleteSpec = (deviceIdx: number, specIdx: number) => {
    if (!schemeData || !schemeData.parts) return;
    if (isSelectionComplete(schemeData)) return;
    if (!window.confirm('Удалить эту комплектующую?')) return;
    
    const updatedDevices = [...schemeData.parts.devices];
    const updatedSpecs = updatedDevices[deviceIdx].specs.filter((_, idx) => idx !== specIdx);
    updatedDevices[deviceIdx] = {
      ...updatedDevices[deviceIdx],
      specs: updatedSpecs
    };
    
    const updatedScheme = {
      ...schemeData,
      parts: {
        devices: updatedDevices
      }
    };
    
    updateSchemeInPDResult(updatedScheme);
  };

  const addSpec = (deviceIdx: number) => {
    if (!schemeData?.parts) return;
    if (isSelectionComplete(schemeData)) return;
    
    const newSpec: SpecItem = {
      name: 'Новая комплектующая',
      quantity: 1,
      uom: 'шт'
    };
    
    const updatedDevices = [...schemeData.parts.devices];
    updatedDevices[deviceIdx] = {
      ...updatedDevices[deviceIdx],
      specs: [...updatedDevices[deviceIdx].specs, newSpec]
    };
    
    const updatedScheme = {
      ...schemeData,
      parts: {
        devices: updatedDevices
      }
    };
    
    updateSchemeInPDResult(updatedScheme);
  };

  const handleCropComplete = async (croppedBlobs: Blob[]) => {
    if (!appId || !schemeData) return;
    
    setIsPreparingChunks(true);
    
    // Предупреждение пользователя при попытке покинуть страницу
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'Идет подготовка к обработке. Вы уверены что хотите покинуть страницу?';
    };
    
    window.addEventListener('beforeunload', beforeUnloadHandler);
    
    try {
      const applicationId = Number.parseInt(appId, 10);
      // ИСПОЛЬЗУЕМ правильный номер страницы из структуры, не индекс массива!
      const pageNum = schemeData.page;
      
      // Create FormData with cropped PDF blobs
      const formData = new FormData();
      formData.append('app_id', String(applicationId));
      formData.append('page_number', String(pageNum));
      
      croppedBlobs.forEach((blob, idx) => {
        formData.append('files', blob, `crop_${idx + 1}.pdf`);
      });
      
      // Send to backend for processing
      await ApiService.processSchemePageCrops(applicationId, pageNum, formData);
      
      // СРАЗУ обновляем локальный статус схемы на 'processing'
      if (schemeData && pdResult) {
        const updatedScheme = { ...schemeData, processing_status: 'processing' };
        setSchemeData(updatedScheme);
        
        const updatedPdResult = {
          ...pdResult,
          scheme_pages: pdResult.scheme_pages.map(s => 
            s.page === pageNum ? updatedScheme : s
          )
        };
        setPdResult(updatedPdResult);
      }
      
      setError(null);
      alert('Схема отправлена на обработку!');
      
    } catch (err) {
      console.error('Error processing crops:', err);
      setError(`Ошибка при обработке: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsPreparingChunks(false);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }
  };

  // Функция для скачивания схемы с разметкой
  const downloadSchemeWithMarkup = async () => {
    if (!schemeData?.url || !pdfCropperRef.current) return;
    
    try {
      setSaving(true);
      
      // Получаем объединённый canvas (PDF + прямоугольники разметки)
      const combinedCanvas = pdfCropperRef.current.getCanvasWithPdfAndMarkup();
      
      if (!combinedCanvas) {
        setError('Не удалось получить разметку со схемы');
        setSaving(false);
        return;
      }
      
      // Конвертируем в PNG blob и скачиваем
      combinedCanvas.toBlob((blob: Blob | null) => {
        if (!blob) {
          setError('Ошибка при конвертации разметки');
          setSaving(false);
          return;
        }
        
        // Создаём ссылку для скачивания
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Формируем имя файла с информацией о схеме
        const deviceName = schemeData.device_name ? schemeData.device_name.replaceAll(' ', '_') : 'scheme';
        const fileName = `scheme_page_${schemeData.page}_${deviceName}_marked.png`;
        link.setAttribute('download', fileName);
        
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        
        setSaving(false);
      }, 'image/png');
      
    } catch (err) {
      console.error('Ошибка при скачивании:', err);
      setError('Ошибка при скачивании файла');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <SparklesIcon className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Загрузка схемы...</p>
        </div>
      </div>
    );
  }

  if (error || !schemeData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Схема не найдена'}</p>
          <button
            onClick={() => navigate(`/applications/${appId}/pd-result`)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Вернуться к списку схем
          </button>
        </div>
      </div>
    );
  }

  // Определяем статусы для блокировки UI
  const isProcessing = schemeData?.processing_status === 'processing';
  const isSelectingProducts = status?.processing_status === 'selecting_products';
  const isPreparingOrProcessing = isPreparingChunks || isProcessing || isSelectingProducts;
  const isSelectionCompleted = isSelectionComplete(schemeData);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/applications/${appId}/pd-result`)}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Вернуться к списку схем
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <DocumentIcon className="h-8 w-8 mr-3" />
                Страница {schemeData.page}
                {schemeData.device_name && ` - ${schemeData.device_name}`}
              </h1>
              {schemeData.qty && (
                <p className="text-gray-600 mt-2">Количество: {schemeData.qty}</p>
              )}
              
              {/* Статус разметки схемы */}
              {(() => {
                const status = getMarkupStatus(schemeData);
                const fullWarningText = `Для этого наведите курсор мыши на область схемы и выделите области, являющиеся значимыми элементами схемы, отделяя несущественные части. 
Рекомендуется производить разметку так, чтобы в каждую выделенную область попадало одно или несколько устройств (панели, щиты, шкафы) целиком.
Старайтесь выделять области как можно детальнее, выделяя каждое устройство (панели, шкафы, щиты) отдельно, и точнее, например по границам устройств (панелей, шкафов, щитов), захватывая их наименования.
После выделения всех значимых областей нажмите кнопку "Обработать выделенные области", чтобы запустить процесс распознавания состава комплектующих на схеме.`;
                
                if (status === 'required') {
                  return (
                    <div className="mt-3">
                      <div className="flex items-center text-red-600 font-medium gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                        <span>Необходимо произвести разметку схемы для точного определения состава комплектующих на ней.</span>
                        <button 
                          onClick={() => setShowMarkupInstructions(!showMarkupInstructions)}
                          className="text-blue-600 hover:text-blue-800 underline text-sm font-semibold ml-1 cursor-pointer"
                        >
                          инструкция
                        </button>
                      </div>
                      {showMarkupInstructions && (
                        <div className="mt-3 p-4 bg-gray-900 text-white rounded-lg text-sm whitespace-pre-wrap border-l-4 border-red-600">
                          <div className="mb-3">{fullWarningText}</div>
                          <div className="flex gap-2 pt-3 border-t border-gray-700">
                            <button
                              onClick={() => setShowExampleModal(true)}
                              className="text-blue-400 hover:text-blue-300 underline text-sm font-semibold cursor-pointer"
                            >
                              Пример разметки
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                
                if (status === 'complete') {
                  return (
                    <div className="mt-3 flex items-center text-green-600 font-medium">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      <span>Разметка схемы произведена</span>
                    </div>
                  );
                }
                
                if (status === 'not_needed') {
                  return (
                    <div className="mt-3 flex items-center text-green-600 font-medium">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      <span>Разметка схемы не требуется</span>
                    </div>
                  );
                }
                
                return null;
              })()}
            </div>
            {saving && (
              <div className="flex items-center text-blue-600">
                <SparklesIcon className="h-5 w-5 animate-spin mr-2" />
                Сохранение...
              </div>
            )}
          </div>
        </div>

        {/* Main Layout: Wide left side + Right sidebar */}
        <div className="flex gap-6 min-h-screen">
          {/* Left: Scheme Image/PDF - Wide */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow p-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Схема</h2>
                <button
                  onClick={downloadSchemeWithMarkup}
                  className="flex items-center px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition text-sm"
                  title="Скачать схему"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                  Скачать
                </button>
              </div>
              {isPreparingOrProcessing ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <SparklesIcon className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                  <p className="text-gray-700 font-medium">
                    {isPreparingChunks 
                      ? 'Не закрывайте страницу. Идет подготовка к обработке...'
                      : isSelectingProducts
                      ? 'Идет подбор комплектующих. Дождитесь завершения...'
                      : 'Схема обрабатывается на сервере...'}
                  </p>
                </div>
              ) : pdfData ? (
                <div className="border border-gray-300 rounded overflow-hidden flex-1">
                  <PdfCropperViewer 
                    ref={pdfCropperRef}
                    pdfData={pdfData} 
                    schemePageNumber={schemeData.page}
                    onCropSubmit={handleCropComplete} 
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center flex-1 bg-gray-100 rounded">
                  <p className="text-gray-500">Схема загружается...</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Devices and Components - Narrow sidebar */}
          <div className="w-96">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  Устройства и комплектующие
                </h2>
                <button
                  onClick={addDevice}
                  disabled={isSelectionCompleted || isSelectingProducts}
                  className={`flex items-center px-3 py-2 rounded transition text-sm ${
                    isSelectionCompleted || isSelectingProducts
                      ? 'bg-gray-300 text-gray-600 cursor-not-allowed' 
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                  title={isSelectionCompleted ? 'Редактирование недоступно после подбора' : ''}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Добавить
                </button>
              </div>

              {/* Показываем сообщение при подборе комплектующих */}
              {isSelectingProducts && (
                <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="text-blue-700 font-medium text-sm">
                    ⏳ Идет подбор комплектующих. Редактирование временно недоступно.
                  </p>
                </div>
              )}

              {/* Показываем сообщение если подбор завершен */}
              {isSelectionCompleted && !isSelectingProducts && (
                <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-500 rounded">
                  <p className="text-amber-700 font-medium text-sm">
                    ⚠️ Подбор комплектующих уже произведен. Для внесения изменений перейдите в панель подбора.
                  </p>
                </div>
              )}

              {/* Badge and button if selection is complete */}
              {isSelectionComplete(schemeData) && (
                <div className="mb-4 space-y-2">
                  <div className="flex items-center justify-center p-2 bg-green-50 border border-green-200 rounded">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-sm font-medium text-green-700">
                      Подбор комплектующих выполнен
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/applications/${appId}`)}
                    className="w-full flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition text-sm"
                  >
                    <DocumentIcon className="h-4 w-4 mr-2" />
                    Перейти на панель подбора
                  </button>
                </div>
              )}

              <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
                {schemeData.parts && schemeData.parts.devices.length > 0 ? (
                  <div className="space-y-3">
                    {schemeData.parts.devices.map((device, deviceIdx) => (
                      <div key={deviceIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Device Header */}
                        <div className="bg-gray-50 p-3">
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => toggleDevice(deviceIdx)}
                              className="flex items-center flex-1 text-left"
                            >
                              {expandedDevices.has(deviceIdx) ? (
                                <ChevronDownIcon className="h-5 w-5 text-gray-600 mr-2" />
                              ) : (
                                <ChevronRightIcon className="h-5 w-5 text-gray-600 mr-2" />
                              )}
                              {editingDevice === deviceIdx ? (
                                <div className="flex items-center flex-1 gap-2" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    value={editValues.name}
                                    onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="Название устройства"
                                  />
                                  <input
                                    type="number"
                                    value={editValues.qty}
                                    onChange={(e) => setEditValues({ ...editValues, qty: parseInt(e.target.value) })}
                                    className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="Кол-во"
                                  />
                                  <button
                                    onClick={() => saveDeviceEdit(deviceIdx)}
                                    className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                                  >
                                    <CheckIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="p-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between flex-1">
                                  <span className="font-medium text-gray-800">
                                    {device.device_name}
                                  </span>
                                  {device.qty !== undefined && (
                                    <span className="text-sm text-gray-600 ml-2">
                                      ×{device.qty}
                                    </span>
                                  )}
                                </div>
                              )}
                            </button>
                            {editingDevice !== deviceIdx && (
                              <div className="flex items-center gap-1 ml-2">
                                <button
                                  onClick={() => startEditDevice(deviceIdx, device)}
                                  disabled={isSelectionCompleted}
                                  className={`p-1 rounded ${
                                    isSelectionCompleted
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-blue-600 hover:bg-blue-50'
                                  }`}
                                  title={isSelectionCompleted ? 'Редактирование недоступно после подбора' : 'Редактировать'}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => deleteDevice(deviceIdx)}
                                  disabled={isSelectionCompleted}
                                  className={`p-1 rounded ${
                                    isSelectionCompleted
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-red-600 hover:bg-red-50'
                                  }`}
                                  title={isSelectionCompleted ? 'Удаление недоступно после подбора' : 'Удалить'}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Device Specs */}
                        {expandedDevices.has(deviceIdx) && (
                          <div className="p-3 bg-white">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                Комплектующие:
                              </span>
                              <button
                                onClick={() => addSpec(deviceIdx)}
                                disabled={isSelectionCompleted}
                                className={`flex items-center text-xs px-2 py-1 rounded ${
                                  isSelectionCompleted
                                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                                title={isSelectionCompleted ? 'Добавление недоступно после подбора' : ''}
                              >
                                <PlusIcon className="h-3 w-3 mr-1" />
                                Добавить
                              </button>
                            </div>
                            {device.specs.length > 0 ? (
                              <div className="space-y-2">
                                {device.specs.map((spec, specIdx) => (
                                  <div
                                    key={specIdx}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                                  >
                                    {editingSpec?.deviceIdx === deviceIdx && editingSpec?.specIdx === specIdx ? (
                                      <div className="flex items-center gap-2 flex-1">
                                        <input
                                          type="text"
                                          value={editValues.name}
                                          onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                                          placeholder="Название"
                                        />
                                        <input
                                          type="number"
                                          value={editValues.quantity}
                                          onChange={(e) => setEditValues({ ...editValues, quantity: parseInt(e.target.value) })}
                                          className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                          placeholder="Кол-во"
                                        />
                                        <input
                                          type="text"
                                          value={editValues.uom}
                                          onChange={(e) => setEditValues({ ...editValues, uom: e.target.value })}
                                          className="w-16 px-2 py-1 border border-gray-300 rounded text-xs"
                                          placeholder="Ед."
                                        />
                                        <button
                                          onClick={() => saveSpecEdit(deviceIdx, specIdx)}
                                          className="p-1 bg-green-500 text-white rounded hover:bg-green-600"
                                        >
                                          <CheckIcon className="h-3 w-3" />
                                        </button>
                                        <button
                                          onClick={cancelEdit}
                                          className="p-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                                        >
                                          <XMarkIcon className="h-3 w-3" />
                                        </button>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="flex-1">
                                          <span className="text-gray-800">{spec.name}</span>
                                          <span className="text-gray-600 ml-2">
                                            - {spec.quantity} {spec.uom}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <button
                                            onClick={() => startEditSpec(deviceIdx, specIdx, spec)}
                                            disabled={isSelectionCompleted}
                                            className={`p-1 rounded ${
                                              isSelectionCompleted
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-blue-600 hover:bg-blue-50'
                                            }`}
                                            title={isSelectionCompleted ? 'Редактирование недоступно после подбора' : 'Редактировать'}
                                          >
                                            <PencilIcon className="h-3 w-3" />
                                          </button>
                                          <button
                                            onClick={() => deleteSpec(deviceIdx, specIdx)}
                                            disabled={isSelectionCompleted}
                                            className={`p-1 rounded ${
                                              isSelectionCompleted
                                                ? 'text-gray-400 cursor-not-allowed'
                                                : 'text-red-600 hover:bg-red-50'
                                            }`}
                                            title={isSelectionCompleted ? 'Удаление недоступно после подбора' : 'Удалить'}
                                          >
                                            <TrashIcon className="h-3 w-3" />
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-xs italic">Нет комплектующих</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic text-center py-8">
                    Нет устройств на этой схеме
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Example Modal */}
        {showExampleModal && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-[95vw] max-h-[95vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  Пример разметки схемы
                </h2>
                <div className="flex items-center gap-4">
                  {/* Zoom Controls */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setExampleZoom(Math.max(50, exampleZoom - 10))}
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                      title="Уменьшить"
                    >
                      −
                    </button>
                    <span className="w-16 text-center text-sm font-medium">
                      {exampleZoom}%
                    </span>
                    <button
                      onClick={() => setExampleZoom(Math.min(300, exampleZoom + 10))}
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                      title="Увеличить"
                    >
                      +
                    </button>
                    <button
                      onClick={() => setExampleZoom(100)}
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                      title="Сбросить"
                    >
                      100%
                    </button>
                  </div>
                  
                  {/* Close Button */}
                  <button
                    onClick={() => setShowExampleModal(false)}
                    className="p-2 hover:bg-gray-100 rounded transition"
                    title="Закрыть"
                  >
                    <XMarkIcon className="h-6 w-6 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-auto p-6 flex items-center justify-center bg-gray-50">
                <img
                  src="/assets/scheme_example.png"
                  alt="Пример разметки схемы"
                  className="rounded"
                  style={{ 
                    width: `${exampleZoom}%`,
                    height: 'auto',
                    maxWidth: 'none'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemeDetailPage;
